import { useRouter } from 'expo-router';
import { useAuthUrl } from 'features/auth/hooks/useAuthUrl/useAuthUrl';
import fetchAccountLookup from 'features/auth/resetPassword/utils/fetchAccountLookup/fetchAccountLookup';
import fetchForgotPassword from 'features/auth/resetPassword/utils/fetchForgotPassword/fetchForgotPassword';
import { InformationScreenTypeEnum } from 'features/otp/information/InformationScreenTypeEnum';
import { useContext, useState } from 'react';
import { FeatureFlagKey } from 'services/statsig/types';
import { getLambdaHeaders } from 'shared/hooks/auth/useLambdaHeaders';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';
import { navigateToMaxLimitError } from 'shared/utils/errors/navigateToMaxLimitError';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';

export function useAccountLookup() {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { getVaroSessionId, idToken, fetchIovationToken } =
    useContext<AuthContextProps>(AuthContext);
  const forgotPasswordAuthUrl = useAuthUrl(
    FeatureFlagKey.FORGOT_PASSWORD_USE_K8S_AUTH_SERVICE,
  );
  const accountLookupAuthUrl = useAuthUrl(
    FeatureFlagKey.ACCOUNT_LOOKUP_USE_K8S_AUTH_SERVICE,
  );

  const initiate2FA = async (emailAddress: string) => {
    setLoading(true);
    const varoSessionId = await getVaroSessionId();
    const headers = await getLambdaHeaders(idToken, varoSessionId);

    try {
      const { challenge = null } = await fetchForgotPassword(
        forgotPasswordAuthUrl,
        { emailAddress },
        headers,
      );

      setLoading(false);

      if (challenge) {
        router.navigate({
          pathname: '/reset-password/check-email',
          params: {
            is1FA: 'false',
            email: emailAddress,
          },
        });
      } else {
        router.navigate({
          pathname: '/otp/error',
          params: { screenType: InformationScreenTypeEnum.GenericError },
        });
      }
    } catch (error) {
      setLoading(false);

      if (navigateToMaxLimitError(router, error)) {
        return;
      }

      logErrorWithContext('ForgotPassword error in 2FA: ' + error, {
        featureArea: FeatureArea.AUTHENTICATION,
        responsibleTeam: ResponsibleTeam.FRAUD,
        severity: ErrorSeverity.CRITICAL,
      });

      router.navigate({
        pathname: '/otp/error',
        params: { screenType: InformationScreenTypeEnum.GenericError },
      });
    }
  };

  const lookupAccount = async (data: { emailAddress: string }) => {
    setLoading(true);

    const aToken = await fetchIovationToken();
    const varoSessionId = await getVaroSessionId();
    const headers = await getLambdaHeaders(idToken, varoSessionId);

    try {
      const {
        identityId = null,
        message = null,
        allow1FA,
      } = await fetchAccountLookup(
        accountLookupAuthUrl,
        {
          ...data,
          aToken,
        },
        headers,
      );
      setLoading(false);

      if (!identityId && allow1FA) {
        if (message !== null) {
          setError(message as string);
        }

        router.navigate({
          pathname: '/otp/error',
          params: { screenType: InformationScreenTypeEnum.GenericError },
        });
      } else {
        if (allow1FA) {
          // Navigate to confirm identity screen with challenge details
          router.navigate({
            pathname: '/reset-password/confirm-identity',
            params: {
              is1FA: 'true',
              identityId,
              email: data.emailAddress,
            },
          });
        } else {
          void initiate2FA(data.emailAddress);
        }
      }
    } catch (error) {
      setError('error');

      logErrorWithContext('Account lookup error in 2FA: ' + error, {
        featureArea: FeatureArea.AUTHENTICATION,
        responsibleTeam: ResponsibleTeam.FRAUD,
        severity: ErrorSeverity.CRITICAL,
      });

      router.navigate({
        pathname: '/otp/error',
        params: { screenType: InformationScreenTypeEnum.GenericError },
      });
    }
  };

  return { lookupAccount, loading, error };
}
